import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faPlus, faTrashCan, faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import { defaultNumberPerPage } from "../../../assets/data/pagination/pagination";
import Asterisk from "../../../components/common/Asterisk/Asterisk";
import Button from "../../../components/common/Button/Button";
import DataTable from "../../../components/common/DataTable/DataTable";
import { Option } from "../../../components/common/Dropdown/Dropdown";
import ErrorMessages from "../../../components/common/ErrorMessages/ErrorMessages";
import GuidedExperienceWizard from "../../../components/GuidedExperience/GuidedExperienceWizard";
import LoadingSpinner from "../../../components/common/LoadingSpinner/LoadingSpinner";
import ConfirmationDialog from "../../../components/common/Modal/ConfirmationDialog";
import Modal from "../../../components/common/Modal/Modal";
import RadioButton from "../../../components/common/RadioGroup/RadioButton";
import ToolTip from "../../../components/common/ToolTip/ToolTip";
import CommercialUnit from "../../../components/unit/CommercialUnit";
import {
  deleteIncomeUnitById,
  deleteMultipleIncomeUnitsByIds,
  getFullTenantSummaryBySnapshotId,
  getIncomeUnitById,
  postIncomeUnit,
  putIncomeUnit,
  resetDeleteIncomeUnitById,
  resetGetFullTenantSummaryBySnapshotId,
  resetGetIncomeUnitById,
  resetPostIncomeUnit,
  resetPutIncomeUnit
} from "../../../redux/actions/incomeUnit/incomeUnit";
import {
  getProfileDetails,
  putCommercialFlag,
  resetGetProfileDetails
} from "../../../redux/actions/profile/profileDetails";
import { resetSaving } from "../../../redux/actions/saveData/saveData";
import { createUnitState } from "../../../redux/reducers/getIncomeUnit/createUnitReducer";
import { GetFullTenantSummaryState } from "../../../redux/reducers/getIncomeUnit/getFullTenantSummaryReducer";
import { GetIncomeUnitState } from "../../../redux/reducers/getIncomeUnit/getIncomeUnitReducer";
import { updateUnitState } from "../../../redux/reducers/getIncomeUnit/updateUnitReducer";
import { GetElsLookupsState } from "../../../redux/reducers/getLookups/getElsLookupsReducer";
import { ProfileDetailsState } from '../../../redux/reducers/getProfile/getProfileDetailsReducer';
import { ProfileOverviewState } from "../../../redux/reducers/getProfile/getProfileOverviewReducer";
import { SaveDataState } from "../../../redux/reducers/getSaveData/saveDataReducer";
import { SaveResultState } from '../../../redux/reducers/getSaveData/saveResultReducer';
import {
  selectDeleteIncomeUnit,
  selectELSLookups,
  selectGetFullTenantSummary,
  selectGetIncomeUnit,
  selectPostIncomeUnit,
  selectProfileGetDetails,
  selectProfileGetOverview,
  selectPutIncomeUnit,
  selectSaveData,
  selectSaveResult
} from "../../../redux/selectors/exportSelectorPaths";
import { BaseAsyncReducerState } from "../../../redux/types";
import { WizardExitTrigger, WizardInteractionEventPayload } from "../../../types/GA4Events/WizardEventParams";
import {
  CommercialFlagType,
  initialCommercialFlag,
  keysOfCommercialFlagType
} from "../../../types/profileDetails/CommercialFlagType";
import { pickFromProfileDetailsType } from "../../../types/profileDetails/ProfileDetailsType";
import IncomeUnitType, { getDefaultUnit } from "../../../types/unit/IncomeUnit";
import {
  getFormErrorByName,
  getFormErrorMessageByName,
  hasError,
  isValidationResult,
  useFormErrorContext
} from "../../../utils/context/FormErrorContext";
import { useNavigation } from "../../../utils/context/NavigationContext";
import { ColumnInfo, FilterColumnInfo, SortingColumnInfo } from "../../../utils/DataTable/ColumnInfo";
import GenericFilter,
{ Filter, filterData, filterOptions } from "../../../utils/DataTable/GenericFilter/GenericFilter";
import GenericSortingTableHeader from "../../../utils/DataTable/GenericSorting/GenericSortingTableHeader";
import { commaSeparate, formatCurrency } from "../../../utils/formatter";
import trackWizardEvent, { createWizardEventPayload } from "../../../utils/GA4EventHelpers/trackWizardEvent";
import { useReadOnly } from "../../../utils/ReadOnlyContext";
import { sortData, SortRule } from "../../../utils/sorting";
import 'react-toastify/dist/ReactToastify.css';
import './Dropdown.css';
import './LeaseInfo.css';
import { useFormWarningContext } from "../../../utils/context/FormWarningContext";

export default function LeaseInfo(): JSX.Element {
  const dispatch = useDispatch()
  const { addError, clearErrors, errors, removeError } = useFormErrorContext();
  const { addWarning, clearWarnings, warnings } = useFormWarningContext();
  const { direction } = useNavigation()
  const { isReadOnly } = useReadOnly()
  const { i18n } = useTranslation()
  const { t } = useTranslation(["lease_table", "common"])

  const createUnitState: createUnitState = useSelector(selectPostIncomeUnit)
  const deleteIncomeUnitState: BaseAsyncReducerState = useSelector(selectDeleteIncomeUnit)
  const elsLookupsState: GetElsLookupsState = useSelector(selectELSLookups)
  const getIncomeUnitState: GetIncomeUnitState = useSelector(selectGetIncomeUnit)
  const getTenantSummaryState: GetFullTenantSummaryState = useSelector(selectGetFullTenantSummary)
  const profileDetails: ProfileDetailsState = useSelector(selectProfileGetDetails)
  const profileOverview: ProfileOverviewState = useSelector(selectProfileGetOverview)
  const saveData: SaveDataState = useSelector(selectSaveData)
  const saveResultState: SaveResultState = useSelector(selectSaveResult)
  const updateUnitState: updateUnitState = useSelector(selectPutIncomeUnit)
  const [warningsShown, setWarningsShown] = useState(false);
  const [getProfileDetailsDispatched, setGetProfileDetailsDispatched] = useState(false)
  const [commercialFlagData, setCommercialFlagData] = useState<CommercialFlagType>({ ...initialCommercialFlag })
  const [initialGetTenantsDispatched, setInitialGetTenantsDispatched] = useState(false)
  const [data, setData] = useState<IncomeUnitType[]>([])
  const [displayedUnits, setDisplayedUnits] = useState<IncomeUnitType[]>([])
  const [refreshDisplayedUnits, setRefreshDisplayedUnits] = useState<boolean>(false)
  const [searchInput, setSearchInput] = useState<string>("")
  const [searchFilter, setSearchFilter] = useState<Filter<IncomeUnitType> | null>(null)
  const [filters, setFilters] = useState<Filter<IncomeUnitType>[]>([])
  const [sortRules, setSortRules] = useState<SortRule<IncomeUnitType>[]>([])
  const [currentPage, setCurrentPage] = useState(1)
  const [numberPerPage, setNumberPerPage] = useState(defaultNumberPerPage)
  const [currentUnit, setCurrentUnit] = useState<IncomeUnitType>(getDefaultUnit())
  const [gettingUnit, setGettingUnit] = useState(false)
  const [showWizardModal, setShowWizardModal] = useState<boolean>(() => {
    let wizardNeverShowed = true
    const currentRollNumber = sessionStorage.getItem("rollNumber")
    if (currentRollNumber) {
      const commericalWizardRolls = sessionStorage.getItem("commericalWizard")
      if (commericalWizardRolls) {
        const rolls = commericalWizardRolls.split(",")
        if (rolls.includes(currentRollNumber)) {
          wizardNeverShowed = false
        }
      }
    }
    return wizardNeverShowed
  })
  const [showAddUnitModal, setShowAddUnitModal] = useState(false)
  const [showEditUnitModal, setShowEditUnitModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [confirmBody, setConfirmBody] = useState(<></>)
  const [confirmAction, setConfirmAction] = useState(() => (() => { /* */ }))

  const [activeWizardStep, setActiveWizardStep] = useState(0);
  const unitHasErrors = useMemo(() => {
    const createHasErrors =
      createUnitState.result != null &&
      isValidationResult(createUnitState.result) &&
      createUnitState.result.errors.length > 0;

    const updateHasErrors =
      updateUnitState.result != null &&
      isValidationResult(updateUnitState.result) &&
      updateUnitState.result.errors.length > 0;

    const formHasErrors = errors.length > 0;

    return createHasErrors || updateHasErrors || formHasErrors;
  }, [
    createUnitState.result,
    updateUnitState.result,
    errors.length,
  ]);

  const unitHasWarnings = useMemo(() => {
    const createHasWarnings =
      createUnitState.result != null &&
      isValidationResult(createUnitState.result) &&
      createUnitState.result.warnings.length > 0;

    const updateHasWarnings =
      updateUnitState.result != null &&
      isValidationResult(updateUnitState.result) &&
      updateUnitState.result.warnings.length > 0;

    const formHasWarnings = warnings.length > 0;

    return createHasWarnings || updateHasWarnings || formHasWarnings;
  }, [
    createUnitState.result,
    updateUnitState.result,
    warnings.length,
  ]);

  useEffect(() => {
    if (!unitHasErrors && unitHasWarnings) {
      setWarningsShown(true);
    }
  }, [unitHasWarnings, unitHasErrors]);

  const columns: ColumnInfo[] = [
    {
      id: "tenantName",
      display: t('headings.tenant-name'),
      extraDisplay: <Asterisk />,
      tooltip: t('tooltips.tenant-name-tooltip'),
    },
    {
      id: "tenantUnitNumber",
      display: t('headings.unit-number'),
      extraDisplay: <Asterisk />,
      tooltip: t('tooltips.unit-number-tooltip'),
    },
    {
      id: "floorLevel",
      display: t('headings.floor-level'),
      extraDisplay: <Asterisk />,
      tooltip: t('tooltips.floor-level-tooltip'),
      dataLkpFunc: (val: unknown) => floorLevelOptions.find((option) => String(option.value) === String(val))?.[i18n.language === "fr" ? "frenchText" : "text"],
    },
    {
      id: "unitSubtypeCode",
      display: t('headings.unit-use'),
      extraDisplay: <Asterisk />,
      tooltip: t('tooltips.unit-use-tooltip'),
      dataLkpFunc: (val: unknown) => subTypeCodeOptions.find((option) => String(option.value) === String(val))?.[i18n.language === "fr" ? "frenchText" : "text"],
      filterDropdown: (([{text: "Blank (Null)", value: "null"}] as Option[]).concat(elsLookupsState.dropdowns["unitSubtypeCodeOptions"] as Option[])).filter(opt => opt && opt.value !== "" && data.map(unit => String(unit.unitSubtypeCode)).includes(opt.value as string))
      
    },
    {
      id: "startDate",
      display: t('headings.start-date'),
      extraDisplay: <Asterisk />,
      tooltip: t('tooltips.start-date-tooltip'),
      type: "date",
    },
    {
      id: "endDate",
      display: t('headings.end-date'),
      tooltip: t('tooltips.end-date-tooltip'),
      type: "date",
    },
    {
      id: "leaseTypeCode",
      display: t('headings.lease-type'),
      extraDisplay: <Asterisk />,
      tooltip: t('tooltips.lease-type-tooltip'),
      dataLkpFunc: (val: unknown) => leaseTypeCodeOptions.find((option) => String(option.value) === String(val))?.[i18n.language === "fr" ? "frenchText" : "text"],
      filterDropdown: (([{text: "Blank (Null)", value: "null"}] as Option[]).concat(elsLookupsState.dropdowns["leaseTypeCodeOptions"] as Option[])).filter(opt => opt && opt.value !== "" && data.map(unit => String(unit.leaseTypeCode)).includes(opt.value as string))
    },
    {
      id: "leaseStatusCode",
      display: t('headings.lease-status'),
      extraDisplay: <Asterisk />,
      tooltip: t('tooltips.lease-status-tooltip'),
      dataLkpFunc: (val: unknown) => leaseStatusCodeOptions.find((option) => String(option.value) === String(val))?.[i18n.language === "fr" ? "frenchText" : "text"],
      filterDropdown: (([{text: "Blank (Null)", value: "null"}] as Option[]).concat(elsLookupsState.dropdowns["leaseStatusCodeOptions"] as Option[])).filter(opt => opt && opt.value !== "" && data.map(unit => String(unit.leaseStatusCode)).includes(opt.value as string))
    },
    {
      id: "netLeasableArea",
      display: t('headings.leased-area'),
      extraDisplay: <Asterisk />,
      tooltip: t('tooltips.leased-area-tooltip'),
      type: "number",
    },
    {
      id: "currentBaseRent",
      display: t('headings.current-base-rent'),
      extraDisplay: <Asterisk />,
      tooltip: t('tooltips.current-base-rent-tooltip'),
      type: "number",
    },
    {
      filterDisabled: true,
      sortingDisabled: true,
      rowActions: [
        (row) => {
          const rowData = row as IncomeUnitType
          return (
            <Button
              forceEnabled={true}
              onClick={(e) => { getUnit(e, String(rowData.snapshotId), String(rowData.tenantId)) }}
              text={<FontAwesomeIcon icon={faPenToSquare} />}
              className={"edit-btn"}
              ariaLabel={t("edit") + " " + String(rowData.tenantId)}
            />
          )
        },
      ],
    },
    {
      filterDisabled: true,
      sortingDisabled: true,
      rowActions: [
        (row) => {
          const rowData = row as IncomeUnitType
          return (
            <Button
              onClick={() => { confirmRemoveUnit(String(rowData.snapshotId), String(rowData.tenantId)) }}
              text={<FontAwesomeIcon icon={faTrashCan} />}
              className={isReadOnly ? "delete-btn-off" : "delete-btn"}
              ariaLabel={t("remove") + " " + String(rowData.tenantId)}
              disabled={isReadOnly}
            />
          )
        }
      ],
    }
  ]
  const currencySymbol = "$"
  const tenantSummaryType = "1" // PIER2-1317: When UI sends tenantSummaryType = 1 to the backend, both unit type 1 and 2 on the snapshot are returned from backend
  const tenantSummaryTableContainerClassName = "tenant-summary-table-container"
  const currentSnapshotId = sessionStorage.getItem("currentSnapshotId") as string
  const propertyCode = sessionStorage.getItem("propertyCode") as string
  const profileTypeCode = profileOverview.profile.profile_type_code.toUpperCase()
  const showCommercialQuestion = (["GLF", "LLE", "HTL", "MTL", "RHO", "LTC"].includes(profileTypeCode)
    || (profileTypeCode === "BIP" && ["340", "341", "352", "361", "602", "470", "472", "478"].includes(propertyCode)))
  const floorLevelOptions = ((elsLookupsState.dropdowns["floorLevelOptions"] as Option[]).filter(opt => opt && opt.value !== ""))
  const subTypeCodeOptions = ((elsLookupsState.dropdowns["unitSubtypeCodeOptions"] as Option[]).filter(opt => opt && opt.value !== ""))
  const leaseTypeCodeOptions = ((elsLookupsState.dropdowns["leaseTypeCodeOptions"] as Option[]).filter(opt => opt && opt.value !== ""))
  const leaseStatusCodeOptions = ((elsLookupsState.dropdowns["leaseStatusCodeOptions"] as Option[]).filter(opt => opt && opt.value !== ""))
  const filterColumns: FilterColumnInfo[] = columns.filter((column) => column.filterDisabled !== true && (column.id || column.filterId))
  const sortingColumns: SortingColumnInfo[] = columns


  const handleActiveWizardStep = (step: number) => setActiveWizardStep(step);

  const undefToNull = (unit: Record<string, unknown>) => {
    const normalizedUnit = { ...unit }
    for (const [key, value] of Object.entries(normalizedUnit)) {
      if (value === undefined) {
        normalizedUnit[key] = null
      }
    }
    return normalizedUnit
  }

  const formatOutput = (value: unknown) => {
    if (value == null || value === undefined) {
      return ""
    } else {
      return value.toString()
    }
  }

  const buildTableContent = (): JSX.Element => {
    const windowWidth = window.innerWidth

    const getTextWidth = (text: string, font: string) => {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (context) {
        context.font = font || getComputedStyle(document.body).font

        return context.measureText(text).width
      }
      else {
        return 0
      }
    }

    let maxTenantNameWidth = 0
    for (let index = numberPerPage * (currentPage - 1);
      index < Math.min(displayedUnits.length, numberPerPage * currentPage);
      index++) {
      const tenantName = displayedUnits[index]["tenantName"]
      if (tenantName) {
        const tenantNameWidth = getTextWidth(tenantName as string, "")
        maxTenantNameWidth = Math.max(maxTenantNameWidth, tenantNameWidth)
      }
    }

    const getTdClassName = (output: string, id: string, ts: IncomeUnitType) => {
      const alignCenter = "text-center"
      const alignLeft = "text-left"
      const alignRight = "text-right"
      const highlightClassName = "highlighted-mandatory-cell"

      let alignClass = ""
      if (["startDate", "endDate"].includes(id)) {
        alignClass = alignCenter
      } else if (["netLeasableArea", "currentBaseRent"].includes(id)) {
        alignClass = alignRight
      } else {
        alignClass = alignLeft
      }

      let highlightClass = ""

      if (id === "endDate" && (ts.endDate !== undefined && ts.endDate !== null)) {
        // Interpret date string as local midnight not UTC midnight
        const [year, month, day] = (ts.endDate as string).split("-").map(Number)
        const date = new Date(year, month - 1, day)
        const today = new Date()

        today.setHours(0, 0, 0, 0)

        if (date < today) {
          highlightClass = highlightClassName
        }
      }

      // Highlight negative GLA entry
      if (id === "netLeasableArea" && (ts.netLeasableArea !== undefined && ts.netLeasableArea !== null)) {
        if (Math.sign(Number(ts.netLeasableArea)) === -1) {
          highlightClass = highlightClassName
        }
      }

      if (!output) {
        if (id === "leaseTypeCode") {
          if ((String(ts.leaseStatusCode) !== "1" && String(ts.leaseStatusCode) !== "2")) {
            highlightClass = highlightClassName
          }
        } else if ([
          "tenantName",
          "tenantUnitNumber",
          "floorLevel",
          "startDate",
          "leaseStatusCode",
          "netLeasableArea",
          "unitSubtypeCode"
        ].includes(id)) {
          highlightClass = highlightClassName
        } else if (id === "currentBaseRent" && ts.leaseStatusCode !== "1") {
          highlightClass = highlightClassName
        }
      }

      let wrapClass = ""
      if (!["tenantName"].includes(id)) {
        wrapClass = "no-wrap"
      }

      let widthClass = ""
      if (["tenantName"].includes(id)) {
        const minWidthClassName = id + "-min-width"
        const minWidthVarName = "--" + minWidthClassName
        const minWidthStyle = getComputedStyle(document.body).getPropertyValue(minWidthVarName)
        if (minWidthStyle) {
          let minWidthInPix = 0
          const strMinWidth = minWidthStyle.toString()
          let strMinWidthValue = ""
          let minWidthValue = 0
          if (strMinWidth.endsWith("vw")) {
            strMinWidthValue = strMinWidth.substring(0, strMinWidth.length - 2)
            minWidthValue = Number(strMinWidthValue)
            minWidthInPix = Math.ceil(minWidthValue * windowWidth / 100)
          } else if (strMinWidth.endsWith("px")) {
            strMinWidthValue = strMinWidth.substring(0, strMinWidth.length - 2)
            minWidthValue = Number(strMinWidthValue)
            minWidthInPix = Math.ceil(minWidthValue)
          } else if (strMinWidth.endsWith("rem")) {
            strMinWidthValue = strMinWidth.substring(0, strMinWidth.length - 3)
            minWidthValue = Number(strMinWidthValue)
            const fontSizeStyle = getComputedStyle(document.documentElement).getPropertyValue("font-size")
            const iFontSize = Number(fontSizeStyle.substring(0, fontSizeStyle.length - 2))
            minWidthInPix = Math.ceil(minWidthValue * iFontSize)
          } else if (strMinWidth.endsWith("em")) {
            strMinWidthValue = strMinWidth.substring(0, strMinWidth.length - 2)
            minWidthValue = Number(strMinWidthValue)
            const fontSizeStyle = getComputedStyle(document.body).getPropertyValue("font-size")
            const iFontSize = Number(fontSizeStyle.substring(0, fontSizeStyle.length - 2))
            minWidthInPix = Math.ceil(minWidthValue * iFontSize)
          }
          if ((maxTenantNameWidth + 8) < minWidthInPix) {
            widthClass = "no-wrap"
          } else {
            widthClass = minWidthClassName
          }
        }
      }

      const columnClass = id
      const className = [highlightClass, columnClass, wrapClass, alignClass, widthClass].join(" ").replaceAll("  ", " ")
      return className.trim()
    }

    const tableBody: JSX.Element[] = []
    for (let index = numberPerPage * (currentPage - 1);
      index < Math.min(displayedUnits.length, numberPerPage * currentPage);
      index++) {
      const row = displayedUnits[index]

      tableBody.push(
        <tr key={`table-row-${row.tenantId}`}>
          {
            columns.map((column, columnIndex) => {
              if (column && Object.keys(column).length > 0 && column["id"]) {
                const id = column["id"] as string
                let dataValue = row[id as keyof IncomeUnitType]
                switch (id) {
                  case "floorLevel":
                  case "unitSubtypeCode":
                  case "leaseTypeCode":
                  case "leaseStatusCode":
                    {
                      if (column.dataLkpFunc) {
                        dataValue = column.dataLkpFunc(dataValue as string) as string
                      } else {
                        dataValue = dataValue as string
                      }
                      break
                    }
                  case "linkFlag":
                    return (
                      <td key={id} className="link-checkbox">
                        <input type="checkbox" checked={Boolean(dataValue)} disabled={true} aria-label="linkFlag" />
                      </td>
                    )
                  case "netLeasableArea":
                    dataValue = commaSeparate(dataValue as number) as string
                    break
                  case "currentBaseRent":
                    dataValue = formatCurrency(dataValue as number, i18n.language) as string
                    break
                  default:
                    break
                }
                const formattedOutput = formatOutput(dataValue)
                if (id === "currentBaseRent") {
                  return <td key={id} className={getTdClassName(formattedOutput, id, row)}>
                    <div className="number-input-container">
                      <div className="number-input-wrapper">
                        <div>{currencySymbol}</div>
                        <div className="width-100">{formattedOutput}</div>
                      </div>
                    </div>
                  </td>
                } else {
                  return <td key={id} className={getTdClassName(formattedOutput, id, row)}>{formattedOutput}</td>
                }
              } else {
                return <td key={"action-" + columnIndex} className="text-center">
                  {
                    column.rowActions && column.rowActions.map((rowAction, index) => {
                      if (typeof rowAction === "function") {
                        return <Fragment key={"action_" + index}>{rowAction(row)}</Fragment>
                      } else {
                        return <Fragment key={"action_" + index}>{rowAction}</Fragment>
                      }
                    })
                  }
                </td>
              }
            }
            )
          }
        </tr>
      )
    }
    return (<>{tableBody}</>)
  }

  const getUnit = (e: React.MouseEvent, snapshotId: string, tenantId: string) => {
    e.preventDefault()
    dispatch(resetGetIncomeUnitById())
    dispatch(getIncomeUnitById(snapshotId, tenantId))
    setGettingUnit(true)
  }

  const updateUnit = (updatedUnit: Record<string, unknown>) => {
    const overrideWarning = warningsShown;
    if (overrideWarning) {
      setWarningsShown(false);
    }

    dispatch(resetPutIncomeUnit())
    dispatch(putIncomeUnit(currentSnapshotId, updatedUnit['tenantId'] as string, {
      "unit": undefToNull(updatedUnit),
      "property_code": sessionStorage.getItem("propertyCode") as string,
      "profile_type_code": profileTypeCode,
      "allow_warning_override": overrideWarning, // PIER2-1703: currently system will override all warnings (including any new ones) if this flag = true. Future enhancement is to track warnings by keys to ensure new ones appear on the UI
      "is_internal": false
    },
    ))
  }

  const createUnit = (createdUnit: Record<string, unknown>) => {
    const overrideWarning = warningsShown;
    if (overrideWarning) {
      if (showAddUnitModal) {
        setWarningsShown(false);
      }
    }
    dispatch(resetPostIncomeUnit())
    dispatch(postIncomeUnit(currentSnapshotId, {
      "unit": undefToNull(createdUnit),
      "property_code": sessionStorage.getItem("propertyCode") as string,
      "profile_type_code": profileTypeCode,
      "allow_warning_override": overrideWarning, // PIER2-1703: currently system will override all warnings (including any new ones) if this flag = true. Future enhancement is to track warnings by keys to ensure new ones appear on the UI
      "is_internal": false
    },
    ))
  }

  const confirmCloseUnitModal = () => {
    const onConfirmCloseUnitModalAction = () => {
      clearErrors();
      clearWarnings();
      setShowConfirmModal(false)
      if (showAddUnitModal) {
        setShowAddUnitModal(false)
      }
      if (showEditUnitModal) {
        setShowEditUnitModal(false)
      }
    }
    if (isReadOnly) {
      onConfirmCloseUnitModalAction()
      return
    }
    setConfirmAction(() => onConfirmCloseUnitModalAction)
    setConfirmBody(<>
      <Trans t={t}
        i18nKey="confirm-save-before-proceed"
        ns="common"
        components={{ p: <p /> }}
      />
    </>)
    setShowConfirmModal(true)
  }

  const confirmRemoveUnit = (snapshotId: string, tenantId: string) => {
    const onConfirmRemoveAction = () => {
      setShowConfirmModal(false)
      dispatch(resetDeleteIncomeUnitById())
      dispatch(deleteIncomeUnitById(snapshotId, tenantId))
    }
    setConfirmAction(() => onConfirmRemoveAction)
    setConfirmBody(<>{t("confirm-remove-unit", { ns: "common" })}</>)
    setShowConfirmModal(true)
  }

  const confirmExitWizardModal = (active_step: number, exit_method: WizardExitTrigger, submitted = false) => {
    if (submitted) {
      setInitialGetTenantsDispatched(false)
      if (showWizardModal) {
        setShowWizardModal(false)
      }
      return
    }

    const onConfirmExitWizardModalAction = () => {
      const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
        wizard_type: "commercial",
        wizard_interaction_type: "exit",
        wizard_step: active_step,
        wizard_exit_trigger: exit_method,
      })
      trackWizardEvent(wizardEventPayload)

      setShowConfirmModal(false)
      if (showWizardModal) {
        setShowWizardModal(false)
      }
    }
    setConfirmAction(() => onConfirmExitWizardModalAction)
    setConfirmBody(<>
      <Trans t={t}
        i18nKey="confirm-exit-wizard"
        ns="common"
        components={{ p: <p /> }}
      />
    </>)
    setShowConfirmModal(true)
  }

  const selectNoCommercialWithUnits = () => {
    removeError(getFormErrorByName(errors, "hasCommercialFlag" as string)?.primaryKey as string)
    const onConfirmNoCommercialWithUnitsAction = () => {
      setShowConfirmModal(false)
      setCommercialFlagData({ hasCommercialFlag: false })
    }
    setConfirmAction(() => onConfirmNoCommercialWithUnitsAction)
    setConfirmBody(<>{t('messages.confirmation-modal')}</>)
    setShowConfirmModal(true)
  }

  const selectNoCommercialWithoutUnits = () => {
    removeError(getFormErrorByName(errors, "hasCommercialFlag" as string)?.primaryKey as string)
    setCommercialFlagData({ hasCommercialFlag: false })
  }

  useEffect(() => {
    if (searchInput) {
      setSearchFilter({
        column: "tenantName",
        value: searchInput,
        method: filterOptions["string"][0],
      })
    } else {
      setSearchFilter(null)
    }
  }, [searchInput])

  useEffect(() => {
    if (!getProfileDetailsDispatched) {
      dispatch(resetGetProfileDetails())
      dispatch(getProfileDetails(currentSnapshotId))
      setGetProfileDetailsDispatched(true)
    }
  }, [currentSnapshotId, dispatch, getProfileDetailsDispatched])

  useEffect(() => {
    if (getProfileDetailsDispatched && !profileDetails.loading) {
      if ((profileDetails.status === 200 && profileDetails.snapshotId === currentSnapshotId)) {
        setCommercialFlagData(pickFromProfileDetailsType(profileDetails.data, keysOfCommercialFlagType))
      } else {
        setGetProfileDetailsDispatched(false)
      }
    }
  }, [currentSnapshotId, dispatch, getProfileDetailsDispatched, profileDetails])

  useEffect(() => {
    if (!initialGetTenantsDispatched) {
      dispatch(resetGetFullTenantSummaryBySnapshotId())
      dispatch(getFullTenantSummaryBySnapshotId(currentSnapshotId, tenantSummaryType))
      setInitialGetTenantsDispatched(true)
    }
  }, [currentSnapshotId, dispatch, initialGetTenantsDispatched])

  useEffect(() => {
    if (initialGetTenantsDispatched && !getTenantSummaryState.loading) {
      if (getTenantSummaryState.status === 200) {
        setData(getTenantSummaryState.tenantSummary)
        setSearchInput("")
        setFilters([])
        setSortRules([])
      } else {
        setInitialGetTenantsDispatched(false)
      }
    }
  }, [getTenantSummaryState, initialGetTenantsDispatched])

  useEffect(() => {
    if (data && data.length > 0) {
      setRefreshDisplayedUnits(true)
    }
  }, [data, filters, searchFilter, sortRules])

  useEffect(() => {
    const getFilterdAndSortedData = () => {
      const forcedSort: SortRule<IncomeUnitType>[] = [
        // { key: (u) => (u.tenantId && u.tenantId < 0 ? 0 : 1)},
        // { key: (u) => (validateDates(u) ? 0 : 1), dir: "asc"},
        // { key: (u) => (u.tenantId && errorousIds.includes(u.tenantId) ? 0 : 1), dir: "asc"},
        // { key: (u) => (u.carryForwardFlag ? 0 : 1), dir: "desc"}
      ]
      if (data && data.length > 0) {
        const extraFilters = []
        if (searchFilter) {
          extraFilters.push(searchFilter)
        }
        const filteredData = filterData(data, [...extraFilters, ...filters])
        const sorted = sortData<IncomeUnitType>(filteredData, [...forcedSort, ...sortRules])
        return sorted
      }
      return []
    }

    if (refreshDisplayedUnits) {
      setRefreshDisplayedUnits(false)
      setDisplayedUnits(getFilterdAndSortedData())
    }
  }, [data, filters, refreshDisplayedUnits, searchFilter, sortRules])

  useEffect(() => {
    const isSavedDataCommercialFlagType = (data: SaveResultState["data"]): data is CommercialFlagType => {
      if (data && typeof data === "object" && "hasCommercialFlag" in data) {
        return true
      }
      return false
    }

    if (saveResultState.loadingCount >= 0 && saveResultState.status) {
      if (saveResultState.status === 200) {
        if (isSavedDataCommercialFlagType(saveResultState.data)) {
          setGetProfileDetailsDispatched(false)
        }
      }
    }
  }, [currentSnapshotId, dispatch, saveResultState])

  useEffect(() => {
    if (!updateUnitState.loading) {
      if (updateUnitState.success) {
        const updateUnitStateData = updateUnitState.data as unknown as IncomeUnitType
        clearErrors();
        clearWarnings();
        setShowEditUnitModal(false)
        setData(prevData =>
          prevData.map(d =>
            d.tenantId === updateUnitStateData.tenantId ? updateUnitStateData : d
          )
        )
        setDisplayedUnits(prevData =>
          prevData.map(d =>
            d.tenantId === updateUnitStateData.tenantId ? updateUnitStateData : d
          ))
        toast.success(t("messages.unit-updated") as string)
      }
      else if (updateUnitState.error) {
        clearErrors();
        clearWarnings();

        if (unitHasErrors) {
          updateUnitState.result.errors.forEach(e => addError(e))
        }
        if (unitHasWarnings) {
          updateUnitState.result.warnings.forEach(e => addWarning(e));
        }

        if (!unitHasErrors && !unitHasWarnings) {
          toast.error(t("messages.unit-update-error") as string)
        }
      }
      dispatch(resetPutIncomeUnit())
    }
  }, [addError, clearErrors, data, dispatch, displayedUnits, t, updateUnitState, addWarning, clearWarnings, currentUnit.tenantId, unitHasErrors, unitHasWarnings])

  useEffect(() => {
    if (!createUnitState.loading) {
      if (createUnitState.success) {
        const createUnitStateData = createUnitState.data as unknown as IncomeUnitType
        clearErrors();
        clearWarnings();
        setShowAddUnitModal(false)
        setData([createUnitStateData, ...data])
        setDisplayedUnits([createUnitStateData, ...displayedUnits])
        toast.success(t("messages.unit-created") as string)
      } else if (createUnitState.error) {
        clearErrors();
        clearWarnings();

        if (unitHasErrors) {
          createUnitState.result.errors.forEach(e => addError(e))
        }
        if (unitHasWarnings) {
          createUnitState.result.warnings.forEach(e => addWarning(e))
        }

        if (!unitHasErrors && !unitHasWarnings) {
          toast.error(t("messages.unit-create-error") as string)
        }
      }
      dispatch(resetPostIncomeUnit())
    }
  }, [addError, clearErrors, createUnitState, data, dispatch, displayedUnits, t, addWarning, clearWarnings, unitHasErrors, unitHasWarnings])

  useEffect(() => {
    if (getIncomeUnitState.loading === false && gettingUnit === true) {
      if (getIncomeUnitState.error === true) {
        toast.error(t("messages.unit-not-found") as string)
        dispatch(resetGetIncomeUnitById())
      } else if (getIncomeUnitState.success === true) {
        setCurrentUnit(getIncomeUnitState.incomeUnitData as unknown as IncomeUnitType)
        setShowEditUnitModal(true)
      }
      setGettingUnit(false)
    }
  }, [dispatch, getIncomeUnitState, gettingUnit, t])

  useEffect(() => {
    if (!deleteIncomeUnitState.loading && deleteIncomeUnitState.success !== undefined) {
      if (deleteIncomeUnitState.success) {
        if (Array.isArray(deleteIncomeUnitState.data)) {
          const deletedTenantIds = deleteIncomeUnitState.data as unknown as number[]
          setData(data.filter((t) => !deletedTenantIds.includes(t.tenantId as number)))
          setDisplayedUnits(displayedUnits.filter((t) => !deletedTenantIds.includes(t.tenantId as number)))
        } else {
          const deletedTenantId = Number(deleteIncomeUnitState.data)
          setData(data.filter((t) => t.tenantId !== deletedTenantId))
          setDisplayedUnits(displayedUnits.filter((t) => t.tenantId !== deletedTenantId))
        }
        toast.success(t("messages.unit-deleted") as string)
      } else {
        toast.error(t("messages.unit-not-deleted") as string)
      }
      dispatch(resetDeleteIncomeUnitById())
    }
  }, [data, deleteIncomeUnitState, dispatch, displayedUnits, t])

  useEffect(() => {
    const submitCommercial = () => {
      const triggerSpectral = direction !== "backward"
      dispatch(putCommercialFlag(currentSnapshotId, {
        "commercial_flag_data": commercialFlagData,
        "spectral_section": profileTypeCode.toLowerCase() + "-comm-units",
        "trigger_spectral": triggerSpectral,
        "property_code": propertyCode,
        "is_internal": false
      }))
      if (showCommercialQuestion && commercialFlagData.hasCommercialFlag === false) {
        const dataTenantIds = data.filter((t) => t.tenantId).map((t) => t.tenantId as number)
        if (dataTenantIds.length > 0) {
          console.log("LeaseInfo delete multiple units because hasCommercialFlag is false")
          dispatch(deleteMultipleIncomeUnitsByIds(currentSnapshotId, dataTenantIds))
        }
      }
    }

    if (saveData.saving) {
      dispatch(resetSaving())
      clearErrors();
      clearWarnings();
      submitCommercial()
    }
  }, [clearErrors, commercialFlagData, currentSnapshotId, data, direction, dispatch, profileTypeCode, propertyCode, saveData.saving, showCommercialQuestion, clearWarnings])

  useEffect(() => {
    const summaryTableContainers = document.getElementsByClassName(tenantSummaryTableContainerClassName)
    if (summaryTableContainers && summaryTableContainers.length > 0) {
      const summaryTableContainer = summaryTableContainers[0]
      summaryTableContainer.scrollTo({
        left: 0,
        behavior: 'auto',
      })
    }
  }, [currentPage])

  return (
    <div className="unit-table-container">
      {(showCommercialQuestion && !commercialFlagData.hasCommercialFlag) &&
        <ErrorMessages errors={errors} />
      }
      {showCommercialQuestion ?
        <div className="lease-info-commercial">
          <p>{t("commercial") as string} <Asterisk /></p>
          <div className="radio-wrapper">
            <span className="side-by-side">
              <RadioButton isError={hasError(errors, "hasCommercialFlag")}
                name="commercial-yes"
                id="commercial-yes"
                text={t("confirm") as string}
                ariaLabel="Yes,has commercial units"
                textPosition="left"
                value="true"
                currentValue={commercialFlagData.hasCommercialFlag}
                onChange={() => {
                  setCommercialFlagData({ hasCommercialFlag: true })
                }} />
              <RadioButton isError={hasError(errors, "hasCommercialFlag")}
                name="commercial-no"
                id="commercial-no"
                text={t("cancel") as string}
                ariaLabel="No, does not have commercial units"
                textPosition="left"
                value="false"
                currentValue={commercialFlagData.hasCommercialFlag}
                onChange={() => {
                  data.length > 0 ?
                    selectNoCommercialWithUnits()
                    :
                    selectNoCommercialWithoutUnits()
                }} />
            </span>
            {
              hasError(errors, "hasCommercialFlag") ?
                <div className="input-error-message radio-error">
                  {getFormErrorMessageByName(errors, "hasCommercialFlag")}
                </div>
                :
                ""
            }
          </div>
        </div>
        :
        <></>
      }
      {commercialFlagData.hasCommercialFlag || (!showCommercialQuestion) ?
        <div className='unit-table-subcontainer'>
          <div className="unit-table-summary-wrapper">
            <div className="unit-table-header-row">
              <div className="disclaimer">
                <p>{t('disclaimer')}</p>
                <p>{t('disclaimer-2')}</p>
              </div>
              <div className="unit-table-control-group">
                {!isReadOnly && <ToolTip contents={
                  <Button onClick={() => {
                    setShowWizardModal(true)
                    const wizardEventPayload: WizardInteractionEventPayload = createWizardEventPayload({
                      wizard_type: "commercial",
                      wizard_interaction_type: "enter",
                      wizard_entry_trigger: "wizard_button_click"
                    })
                    trackWizardEvent(wizardEventPayload)
                  }}
                    text={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                    ariaLabel="guidedExperienceWizard"
                    disabled={false} />
                }>
                  {t("tooltips.wizard-tooltip")}
                </ToolTip>}
                {!isReadOnly && <ToolTip contents={
                  <Button onClick={() => setShowAddUnitModal(true)}
                    text={<FontAwesomeIcon icon={faPlus} />}
                    ariaLabel="addUnit" />
                }>
                  {t('tooltips.add-unit-tooltip')}
                </ToolTip>}
                <GenericFilter columns={filterColumns} filters={filters} setFilters={setFilters} />
                <ToolTip contents={<input name="tenant-search" id="tenant-search" type="search"
                  placeholder={t("search", { ns: "lease_table" })} className="tenant-search-input"
                  onChange={(e) => setSearchInput(e.target.value)} autoComplete="off"
                  value={searchInput === undefined ? "" : searchInput} maxLength={250}
                />}>
                  {t("search", { ns: "lease_table" })}
                </ToolTip>
              </div>
            </div>

            <DataTable thead={<GenericSortingTableHeader columns={sortingColumns}
              sortRules={sortRules} setSortRules={setSortRules} />}
              className="tenant-summary-table"
              tableContainerClassName={tenantSummaryTableContainerClassName}
              totalPages={Math.ceil(displayedUnits.length / numberPerPage)}
              currentPage={currentPage}
              changePage={setCurrentPage}
              numberPerPage={numberPerPage}
              changeNumberPerPage={setNumberPerPage}>
              {buildTableContent()}
            </DataTable>

          </div>
        </div>
        :
        <></>
      }
      {showAddUnitModal &&
        <Modal title={t('headings.add-unit')} onCloseModal={() => confirmCloseUnitModal()}>
          <LoadingSpinner loading={createUnitState.loading}>
            <CommercialUnit unit={getDefaultUnit()}
              onSave={(unit) => createUnit(unit)}
              onCancel={() => confirmCloseUnitModal()}
              showDeleteBtn={false}
              tenantSummaryType={tenantSummaryType}
            />
          </LoadingSpinner>
        </Modal>}
      {showEditUnitModal &&
        <Modal title={currentUnit['tenantName'] as string} onCloseModal={() => confirmCloseUnitModal()}>
          <LoadingSpinner loading={getIncomeUnitState.loading
            || deleteIncomeUnitState.loading
            || updateUnitState.loading
          } >
            <CommercialUnit unit={currentUnit}
              onSave={(unit) => updateUnit(unit)}
              onCancel={() => confirmCloseUnitModal()}
              showDeleteBtn={false}
              tenantSummaryType={tenantSummaryType}
            />
          </LoadingSpinner>
        </Modal>}
      {showWizardModal && !isReadOnly
        && (commercialFlagData.hasCommercialFlag || (!showCommercialQuestion)) &&
        <Modal title={t('headings.guided-experience-wizard')}
          onCloseModal={() => confirmExitWizardModal(activeWizardStep, "x_icon", false)}
          class="guided-experience-wizard-modal">
          <GuidedExperienceWizard snapshotId={Number(currentSnapshotId)}
            onCancel={confirmExitWizardModal}
            sendActiveStepToParent={handleActiveWizardStep}
          />
        </Modal>}
      {showConfirmModal &&
        <ConfirmationDialog
          onCancel={() => { setShowConfirmModal(false) }}
          onConfirm={() => confirmAction()}
          headerClass="no-border" buttonGroupClass="justify-content-center no-border"
        >
          {confirmBody}
        </ConfirmationDialog>
      }
    </div>
  )
}
